import { RootSelector, getStore } from '../../reducers';
import { TagShape } from './model';
import { createSelector } from '@reduxjs/toolkit';
import { Tag } from '../../api';

export const getTag: RootSelector<TagShape> = createSelector(getStore, (state) => state.tag);

export const getTags: RootSelector<Tag[]> = createSelector(getTag, (tag) => tag.tags);

export const getTagsById: RootSelector<Partial<Record<Tag['id'], Tag>>> = createSelector(
  getTags,
  (tags) => {
    const result: Partial<Record<Tag['id'], Tag>> = {};
    tags.forEach((tag) => {
      result[tag.id] = tag;
    });
    return result;
  },
);

export const getSelectedIds: RootSelector<Tag['id'][]> = createSelector(
  getTag,
  (tag) => tag.selectedIds,
);

export const getSelectedIdsIndexed: RootSelector<Partial<Record<Tag['id'], true>>> = createSelector(
  [getTagsById, getSelectedIds],
  (tagsById, selectedIds) => {
    const result: Partial<Record<Tag['id'], true>> = {};
    selectedIds.forEach((id) => {
      result[id] = true;
    });
    return result;
  },
);

export const getExpandedMode: RootSelector<boolean> = createSelector(
  getTag,
  (tag) => tag.expandedMode,
);

export type TagsCompositionItem = { tag: Tag; selected: boolean; related: boolean };
export const getTagsComposition: RootSelector<TagsCompositionItem[]> = createSelector(
  [getTags, getTagsById, getSelectedIds, getSelectedIdsIndexed, getExpandedMode],
  (tags, tagsById, selectedIds, selectedIdsIndexed, expandedMode) => {
    const relatedIdsIndexed: Partial<Record<Tag['id'], true>> = {};
    selectedIds.forEach((selectedId) =>
      // Проходим по всем выбранным тегам, и берем их связанные теги
      tagsById[selectedId]?.related_tag_ids.forEach((id) => {
        if (selectedIdsIndexed[id]) {
          // Если связанный тег сам по себе тоже выбран юзером,
          // то считаем его выбранным, а не связанным.
          return;
        }
        if (!tagsById[id]?.asset_count) {
          // Если нет ассетов, то считаем, что это обычный тэг, а не связанный,
          // т.к. нет смысла выделять для юзера такой тег на фоне обычных.
          return;
        }
        relatedIdsIndexed[id] = true;
      }),
    );

    const items: TagsCompositionItem[] = tags.map((tag) => ({
      tag,
      selected: Boolean(selectedIdsIndexed[tag.id]),
      related: Boolean(relatedIdsIndexed[tag.id]),
    }));

    function sortByName(a: TagsCompositionItem, b: TagsCompositionItem) {
      return a.tag.name.toLowerCase().localeCompare(b.tag.name.toLowerCase());
    }

    items.sort(sortByName);

    let selectedItems: TagsCompositionItem[] = [];
    let relatedItems: TagsCompositionItem[] = [];
    let otherItems: TagsCompositionItem[] = items;

    function partition<T>(items: T[], condition: (item: T) => boolean) {
      const result: [T[], T[]] = [[], []];
      items.forEach((item) => {
        result[condition(item) ? 0 : 1].push(item);
      });
      return result;
    }
    [selectedItems, otherItems] = partition(otherItems, (item) => item.selected);
    [relatedItems, otherItems] = partition(otherItems, (item) => item.related);

    function sortByAssetCount(a: TagsCompositionItem, b: TagsCompositionItem) {
      return b.tag.asset_count - a.tag.asset_count;
    }

    relatedItems.sort(sortByAssetCount);

    if (!expandedMode) {
      otherItems = otherItems.filter(({ tag }) => tag.asset_count);
      otherItems.sort(sortByAssetCount);
    }

    return [...selectedItems, ...relatedItems, ...otherItems];
  },
);

export const getLoading: RootSelector<boolean> = createSelector(getTag, (tag) => tag.loading);
